/**
 * The session: who is signed in, which organization the application is working
 * in, and the three mutations that change either.
 *
 * # Why there is an organization here at all
 *
 * Every useful route in this API is organization-scoped — `/v1/organizations/
 * {org_id}/…` — and there is deliberately no route that lists the
 * organizations a user belongs to (it would be a query across tenants).
 * `GET /v1/users/me` returns `personal_org_id`, which is where the
 * application starts. The user can switch to another organization they are a
 * member of by naming it (`GET /v1/users/me/organizations/{slug}` answers only
 * for members) or by creating one; the choice is remembered per user in this
 * browser and is a convenience, never an authority — every request is still
 * decided by the server.
 */

import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { onUnauthenticated } from '~/api/client';
import * as operations from '~/api/operations';
import { keys, useSession, type SessionState } from '~/api/queries';
import type { ApiError } from '~/api/problem';

/**
 * Every position a visitor can be in, named.
 *
 * Seven, not four, because each of these produces a different screen and
 * collapsing any two of them produces a screen that is wrong for one of the
 * cases it covers:
 *
 * | phase              | what the user sees                                   |
 * |--------------------|------------------------------------------------------|
 * | `unknown`          | the application's own loading shape                   |
 * | `unauthenticated`  | the sign-in form, plain                               |
 * | `session_expired`  | the sign-in form, saying why they are back at it      |
 * | `mfa_required`     | the second-factor challenge                           |
 * | `authenticated`    | the product                                           |
 * | `network_error`    | "cannot reach Exi Git", with a retry                  |
 * | `auth_error`       | "Exi Git is not responding", with a retry             |
 *
 * `session_expired` versus `unauthenticated` is the one that is easy to skip
 * and should not be: somebody who was working a second ago and is suddenly
 * looking at a sign-in form deserves to be told that their session ended
 * rather than left to wonder what they clicked.
 *
 * `network_error` versus `auth_error` matters for the opposite reason — the
 * first is plausibly the user's own connection and worth retrying by hand; the
 * second is ours and is not something they can fix.
 */
export type SessionPhase =
  | 'unknown'
  | 'unauthenticated'
  | 'session_expired'
  | 'mfa_required'
  | 'authenticated'
  | 'network_error'
  | 'auth_error';

interface SessionApi {
  /** Where the visitor is. Switch on this; it covers every case. */
  phase: SessionPhase;
  state: SessionState | undefined;
  /** True only while the very first resolution is in flight. */
  isLoading: boolean;
  /** Set when the API could not be reached or answered 5xx. */
  error: ApiError | null;
  refetch: () => void;

  /** The signed-in user, or undefined in any other state. */
  user: operations.User | undefined;
  /** The organization every scoped request uses. See the note above. */
  orgId: string | undefined;
  /** The caller's personal organization. */
  personalOrgId: string | undefined;
  /** Switch the active organization; `undefined` returns to the personal one. */
  setActiveOrg: (orgId: string | undefined) => void;

  signIn: (credentials: { email: string; password: string }) => Promise<void>;
  verifyMFA: (code: string) => Promise<void>;
  signOut: () => Promise<void>;
  isSigningIn: boolean;
  isSigningOut: boolean;
}

const SessionContext = createContext<SessionApi | null>(null);

export function useSessionContext(): SessionApi {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSessionContext must be used inside <SessionProvider>.');
  }
  return context;
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const query = useSession();
  const [isSigningIn, setSigningIn] = useState(false);
  const [isSigningOut, setSigningOut] = useState(false);
  /**
   * Set when a session we held was refused, cleared when a new one is
   * established or deliberately ended.
   *
   * It is what separates `session_expired` from `unauthenticated`, and it is
   * local state rather than a derived value because by the time the phase is
   * computed the only evidence is gone: the query says `anonymous` either way.
   */
  const [wasRefused, setWasRefused] = useState(false);

  const state = query.data;
  const user = state?.status === 'authenticated' ? state.user : undefined;
  const personalOrgId = user?.personal_org_id;
  const activeOrgKey = user ? `exigit.activeOrg.${user.id}` : undefined;
  // The remembered choice, read when the user is known. Storage can be absent
  // or throw (private windows, blocked site data); the personal organization
  // is the answer then. A choice made in this session overrides it.
  const remembered = useMemo(() => {
    if (!activeOrgKey) return undefined;
    try {
      return window.localStorage.getItem(activeOrgKey) ?? undefined;
    } catch {
      return undefined;
    }
  }, [activeOrgKey]);
  const [chosen, setChosen] = useState<{ key: string; value: string | undefined } | null>(null);
  const activeOrg = chosen && chosen.key === activeOrgKey ? chosen.value : remembered;
  const setActiveOrg = useCallback(
    (next: string | undefined) => {
      if (!activeOrgKey) return;
      setChosen({ key: activeOrgKey, value: next });
      try {
        if (next && next !== personalOrgId) window.localStorage.setItem(activeOrgKey, next);
        else window.localStorage.removeItem(activeOrgKey);
      } catch {
        // A preference that cannot be remembered is still applied now.
      }
    },
    [activeOrgKey, personalOrgId],
  );
  const orgId = activeOrg ?? personalOrgId;

  const phase: SessionPhase = query.isPending
    ? 'unknown'
    : query.error
      ? query.error.isTransport
        ? 'network_error'
        : 'auth_error'
      : state?.status === 'authenticated'
        ? 'authenticated'
        : state?.status === 'mfa_required'
          ? 'mfa_required'
          : wasRefused
            ? 'session_expired'
            : 'unauthenticated';

  /**
   * Sign in, then establish who that was.
   *
   * `POST /v1/auth/sign-in` answers `204` with no body: it sets a cookie and
   * says nothing. So the only way to learn the outcome — and the only way to
   * confirm the browser actually kept the cookie — is to ask. `refetchQueries`
   * rather than `invalidateQueries` because this must *await* the answer:
   * invalidation is fire-and-forget and the caller would navigate before the
   * session was known, landing on a guard that still thinks nobody is signed in.
   */
  const signIn = useCallback<SessionApi['signIn']>(
    async (credentials) => {
      setSigningIn(true);
      setWasRefused(false);
      try {
        await operations.signIn(credentials);
        await queryClient.refetchQueries({ queryKey: keys.currentUser });
      } finally {
        setSigningIn(false);
      }
    },
    [queryClient],
  );

  const verifyMFA = useCallback<SessionApi['verifyMFA']>(
    async (code) => {
      await operations.verifyMFA({ code });
      await queryClient.refetchQueries({ queryKey: keys.currentUser });
    },
    [queryClient],
  );

  /**
   * Sign out, and forget everything.
   *
   * `clear()` and not a targeted invalidation. Every cached page in this
   * application is somebody's data, and leaving a repository list in memory
   * after sign-out means the next person to sign in on this machine sees it
   * for the frame before their own data arrives.
   *
   * The cache is cleared even when the request failed, because a sign-out that
   * leaves the previous user's data on screen is the worse of the two
   * outcomes — and `POST /v1/auth/sign-out` is reachable by a session that has
   * not satisfied MFA precisely so that ending a session is always possible.
   */
  const signOut = useCallback<SessionApi['signOut']>(async () => {
    setSigningOut(true);
    // Leaving deliberately is not the same as being thrown out, and the
    // sign-in form should not claim otherwise.
    setWasRefused(false);
    try {
      await operations.signOut();
    } finally {
      queryClient.clear();
      setSigningOut(false);
    }
  }, [queryClient]);

  /**
   * End the session the moment the server stops accepting it.
   *
   * A 401 can arrive on any request — a session expires, or an operator
   * revokes it, while somebody is reading a page. Without this the user stays
   * inside the authenticated shell: their avatar is still in the top bar, the
   * navigation still works, and each page they open shows "Authentication is
   * required" as though it were that page's problem. Verified against a real
   * server: the application never left that state.
   *
   * The data is written straight into the cache rather than invalidated,
   * because an invalidation refetches `/users/me`, which 401s, which fires
   * this again. Writing `anonymous` settles it in one step and makes the
   * handler idempotent: a second 401 from another in-flight query finds the
   * session already anonymous and returns.
   */
  useEffect(
    () =>
      onUnauthenticated(() => {
        // Read the cache rather than close over `state`: the listener is
        // registered once and `state` in its closure would be whatever it was
        // at registration. The cache is the live answer, and reading it here
        // also keeps anything from being written during render.
        const current = queryClient.getQueryData<SessionState>(keys.currentUser);
        if (current?.status !== 'authenticated' && current?.status !== 'mfa_required') {
          return;
        }
        setWasRefused(true);
        queryClient.setQueryData(keys.currentUser, { status: 'anonymous' } satisfies SessionState);
        // Everything organization-scoped belonged to the session that just
        // ended. Removing rather than invalidating: an invalidation would
        // refetch it all against a session that no longer exists.
        queryClient.removeQueries({ queryKey: ['org'] });
      }),
    [queryClient],
  );

  const refetch = useCallback(() => {
    void query.refetch();
  }, [query]);

  const value = useMemo<SessionApi>(
    () => ({
      phase,
      state,
      isLoading: query.isPending,
      error: query.error,
      refetch,
      user,
      orgId,
      personalOrgId,
      setActiveOrg,
      signIn,
      verifyMFA,
      signOut,
      isSigningIn,
      isSigningOut,
    }),
    [
      phase,
      state,
      query.isPending,
      query.error,
      refetch,
      user,
      orgId,
      personalOrgId,
      setActiveOrg,
      signIn,
      verifyMFA,
      signOut,
      isSigningIn,
      isSigningOut,
    ],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
